import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";
import { sendContact } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await sendContact(form);
      toast.success(res.message || "Message sent!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-8">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5 animate-fade-up">
          <span className="font-sans text-sm uppercase tracking-wider text-[#D96B43]">
            Say hello
          </span>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-[#2C3529] mt-4 leading-[1.05]">
            We'd love to hear from you
          </h1>
          <p className="font-sans text-lg text-[#576054] mt-6 leading-relaxed">
            A question about your companion? A story you'd love us to tell?
            Drop us a note — a real person reads every message.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[#839788]/20 text-[#5C6B5F]">
                <Mail size={20} strokeWidth={1.5} />
              </span>
              <div>
                <p className="font-sans text-xs uppercase tracking-wider text-[#8a9184]">Email</p>
                <p className="font-sans text-[#2C3529]">hello@pawjournal.com</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[#DDA77B]/25 text-[#B07C4F]">
                <MapPin size={20} strokeWidth={1.5} />
              </span>
              <div>
                <p className="font-sans text-xs uppercase tracking-wider text-[#8a9184]">Studio</p>
                <p className="font-sans text-[#2C3529]">Portland, Oregon</p>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-6 md:col-start-7 animate-fade-up" style={{ animationDelay: "0.12s" }}>
          <form
            onSubmit={handleSubmit}
            className="bg-[#FAF9F6] rounded-[2.5rem] p-8 sm:p-10 border border-[#EAE4D9]"
          >
            <div className="space-y-5">
              <div>
                <label className="font-sans text-sm text-[#2C3529] font-medium">Your name</label>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name-input"
                  className="mt-2 w-full rounded-2xl bg-white border border-[#EAE4D9] px-5 py-3 font-sans text-[#2C3529] outline-none focus:ring-2 focus:ring-[#D96B43]/40 focus:border-[#D96B43] transition"
                  placeholder="Jamie Rivers"
                />
              </div>
              <div>
                <label className="font-sans text-sm text-[#2C3529] font-medium">Email address</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email-input"
                  className="mt-2 w-full rounded-2xl bg-white border border-[#EAE4D9] px-5 py-3 font-sans text-[#2C3529] outline-none focus:ring-2 focus:ring-[#D96B43]/40 focus:border-[#D96B43] transition"
                  placeholder="jamie@email.com"
                />
              </div>
              <div>
                <label className="font-sans text-sm text-[#2C3529] font-medium">Message</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message-input"
                  className="mt-2 w-full rounded-2xl bg-white border border-[#EAE4D9] px-5 py-3 font-sans text-[#2C3529] outline-none focus:ring-2 focus:ring-[#D96B43]/40 focus:border-[#D96B43] transition resize-none"
                  placeholder="Tell us about your furry (or feathery) friend..."
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="w-full rounded-full bg-[#D96B43] text-white px-8 py-3.5 font-sans font-medium hover:bg-[#C25D3A] transition-colors duration-300 disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <><Send size={18} /> Send message</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
